import { ReactRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import { i18n } from '@lingui/core';
import tippy from 'tippy.js';
import { CustomAttributeS } from '../../../Service/Context/CustomAttributeS';
import { eventPlaceholder, locationPlaceholder } from '../../../Service/Placeholder/PlaceholderLabels';
import { MentionList } from './MentionList';

const amountPlaceholderIds = [
  'event_hours',
  'event_days',
  'event_nights',
  'location_distance',
  'location_double_distance',
  'location_quadruple_distance',
];

const amountPlaceholderGroups = {
  event_hours: 'Event',
  event_days: 'Event',
  event_nights: 'Event',
  location_distance: 'Entfernung',
  location_double_distance: 'Entfernung',
  location_quadruple_distance: 'Entfernung',
};

const predefinedPlaceholders = [...eventPlaceholder, ...locationPlaceholder].filter(({ placeholder }) =>
  amountPlaceholderIds.includes(placeholder),
);

let customAmountPlaceholdersPromise;
let customAmountPlaceholders = [];

const getCustomAmountPlaceholders = () => {
  if (!customAmountPlaceholdersPromise) {
    customAmountPlaceholdersPromise = CustomAttributeS.fetchQuantityPlaceholdersCandidates()
      .then((attributes) => {
        customAmountPlaceholders = attributes.map((attribute) => ({
          id: `${attribute.groupName.replace(/ /g, '_')}:${attribute.name.replace(/ /g, '_')}`,
          label: attribute.name,
          group: 'Eigene Felder',
        }));
        return customAmountPlaceholders;
      })
      .catch(() => {
        customAmountPlaceholdersPromise = undefined;
        return [];
      });
  }

  return customAmountPlaceholdersPromise;
};

const getDefaultAmountPlaceholders = () =>
  amountPlaceholderIds.map((id) => {
    const placeholder = predefinedPlaceholders.find((candidate) => candidate.placeholder === id);

    return {
      id,
      label: placeholder ? i18n._(placeholder.text) : id,
      group: amountPlaceholderGroups[id],
    };
  });

const filterAmountPlaceholders = (placeholders, query) => {
  const normalizedQuery = query.toLowerCase();

  return placeholders.filter(({ id, label }) => `${label} {${id}}`.toLowerCase().includes(normalizedQuery));
};

const getFilteredAmountPlaceholders = async (query, includeCustomFields) => {
  const customPlaceholders = includeCustomFields ? await getCustomAmountPlaceholders() : [];
  return filterAmountPlaceholders([...getDefaultAmountPlaceholders(), ...customPlaceholders], query);
};

const getImmediatelyAvailableAmountPlaceholders = (query, includeCustomFields) =>
  filterAmountPlaceholders(
    [...getDefaultAmountPlaceholders(), ...(includeCustomFields ? customAmountPlaceholders : [])],
    query,
  );

const findAmountSuggestionMatch = ({ $position }) => {
  let containsPlaceholder = false;

  $position.parent.descendants((node) => {
    if (node.type.name === 'mention') {
      containsPlaceholder = true;
      return false;
    }
    return true;
  });

  if (containsPlaceholder) {
    return null;
  }

  const from = $position.start();
  const query = $position.parent.textContent;

  return {
    range: {
      from,
      to: from + $position.parent.content.size,
    },
    query,
    text: query,
  };
};

export const amountSuggestionPluginKey = new PluginKey('amountSuggestion');

const createAmountSuggestion = (includeCustomFields) => ({
  char: '',
  pluginKey: amountSuggestionPluginKey,
  allowedPrefixes: null,
  findSuggestionMatch: findAmountSuggestionMatch,
  allow: ({ editor }) => editor.isFocused,
  items: ({ query }) => getFilteredAmountPlaceholders(query, includeCustomFields),
  command: ({ editor, range, props }) => {
    editor
      .chain()
      .focus()
      .insertContentAt(range, {
        type: 'mention',
        attrs: props,
      })
      .run();
    editor.commands.blur();
  },
  render: () => {
    let reactRenderer;
    let popup;

    const getReferenceClientRect = (props) => () =>
      props.clientRect?.() ?? props.editor.view.dom.getBoundingClientRect();

    const updateVisibility = (props) => {
      if (!popup) return;
      if (props.items.length > 0) {
        popup[0].show();
      } else {
        popup[0].hide();
      }
    };

    const updatePopup = (props, items = props.items) => {
      if (!reactRenderer || !popup) return;
      const updatedProps = { ...props, items };
      reactRenderer.updateProps(updatedProps);
      popup[0].setProps({
        getReferenceClientRect: getReferenceClientRect(props),
      });
      updateVisibility(updatedProps);
    };

    const isCurrentSuggestion = (props) => {
      const pluginState = amountSuggestionPluginKey.getState(props.editor.state);
      return (
        props.editor.isFocused &&
        pluginState?.active &&
        pluginState.query === props.query &&
        pluginState.range.from === props.range.from &&
        pluginState.range.to === props.range.to
      );
    };

    return {
      onBeforeStart: (props) => {
        const initialProps = {
          ...props,
          items: getImmediatelyAvailableAmountPlaceholders(props.query, includeCustomFields),
        };
        reactRenderer = new ReactRenderer(MentionList, {
          props: initialProps,
          editor: props.editor,
        });

        popup = tippy('body', {
          getReferenceClientRect: getReferenceClientRect(props),
          appendTo: () => document.body,
          content: reactRenderer.element,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
        updateVisibility(initialProps);
      },

      onStart(props) {
        if (isCurrentSuggestion(props)) {
          updatePopup(props);
        }
      },

      onBeforeUpdate(props) {
        updatePopup(props, getImmediatelyAvailableAmountPlaceholders(props.query, includeCustomFields));
      },

      onUpdate(props) {
        if (isCurrentSuggestion(props)) {
          updatePopup(props);
        }
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup[0].hide();
          return true;
        }

        const handled = reactRenderer.ref?.onKeyDown(props);
        if (!handled && props.event.key === 'Enter') {
          props.view.dom.blur();
          return true;
        }

        return handled;
      },

      onExit() {
        popup?.[0].destroy();
        reactRenderer?.destroy();
        popup = undefined;
        reactRenderer = undefined;
      },
    };
  },
});

export default createAmountSuggestion;
